import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import CustomMultiSelect from "../../Common/FormElements/MultiSelect";
import API from "../../../Api/Api";
import { API_END_POINT } from "../../../Api/EndPoints";
import TextInput from "../../Common/FormElements/TextInput/TextInput";
import TextArea from "../../Common/FormElements/TextArea/TextArea";
import Select from "../../Common/FormElements/Select/Select";
import Button from "../../Common/FormElements/Button/Button";
import MagicLoginModal from "../MagicLoginModal";
import {
  sendProjectEmail,
  updateProject,
} from "../../../REDUX/actions/appProjects";
import { getModels, isSpaModel } from "../../../REDUX/actions/model";
import { fetchCommercialOptions } from "../../../REDUX/actions/commercials";
import { closeRightModal } from "../../../REDUX/actions/rightModal";
import { AUTO_SOURCES, prospectSourceOptions } from "../prospectSources";
import { formatEuro } from "./QuotePill";

const toOption = (item) => (item ? { name: item.name, code: item.id } : null);

/**
 * Client sheet: the sale behind the project (read-only — payments and
 * delivery have their own tabs) — model sold, amount HT, the store's order to
 * Poseidon for it and the spa's serial number.
 */
const ClientOrderBlock = ({ sale, t }) => {
  const units = sale.units ?? [];
  const spa = units.find((unit) => unit.productType === 1) ?? units[0];
  const rows = [
    ["Model sold", spa?.model],
    ["Order amount excl. VAT", formatEuro(sale.totalHT)],
    ["Linked Poseidon order", sale.cart?.number],
    ["Serial number", spa?.serialNumber],
  ];
  return (
    <div className="mt-4 p-3 br10 bg-dimGrey">
      <p className="f16 sBold text-blue mb-3">
        {t("Customer order no. {{number}}", { number: sale.orderNumber })}
        {sale.state === "cancelled" && ` · ${t("Cancelled")}`}
        {sale.state === "delivered" && ` · ${t("Delivered")}`}
      </p>
      <div className="row g-3">
        {rows.map(([label, value]) => (
          <div className="col-6" key={label}>
            <p className="f13 text-brightGrey mb-1">{t(label)}</p>
            <p className="f14 sBold mb-0">{value || "-"}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Slide 6 — Général page of a project. Field list & order per the client:
 * N Projet, Nom projet, Nom/Prénom/Mail/Téléphone Principal Projet,
 * Focus Modèle, Modèle intéressé, Contact (source), Marque concurrente,
 * Budget, Commercial(e), Commentaire. Everything editable — main-contact
 * fields save back onto the prospect account.
 */
const ProjectGeneral = ({ project, sale }) => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation("common");
  const { updateLoading } = useSelector((state) => state.appProjectsReducer);
  // A store's commercial follows its projects: it can't hand one over.
  const isCommercial =
    useSelector((state) => state.userReducer.user?.attributes?.userRole) === 7;
  const { models } = useSelector((state) => state.modelReducer) ?? {};
  const [commercialOptions, setCommercialOptions] = useState([]);
  const [showMagicLogin, setShowMagicLogin] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [spaMarques, setSpaMarques] = useState([]);

  const mainContact = project.mainContact ?? {};
  const [formData, setFormData] = useState({
    number: project.number ?? "",
    name: project.name ?? "",
    lastName: mainContact.lastName ?? "",
    firstName: mainContact.firstName ?? "",
    email: mainContact.email ?? "",
    phoneNumber: mainContact.phoneNumber ?? "",
    contact: mainContact.contact ?? "",
    comment: project.comment ?? "",
    budget: project.budget ?? "",
    interestedModels: (project.interestedModels ?? []).map((m) => m.id),
    competitorBrands: (project.competitorBrands ?? []).map((m) => m.id),
    focusModel: toOption(project.focusModel),
    // "Commercial" = a commercial (api::commercial) of the project's store.
    commercial: project.salesRep
      ? {
          name: [project.salesRep.firstName, project.salesRep.lastName]
            .filter(Boolean)
            .join(" "),
          code: project.salesRep.id,
        }
      : null,
  });

  useEffect(() => {
    const controller = new AbortController();
    if (!models?.length) {
      dispatch(getModels(i18n.language?.includes("en") ? "en" : "fr"));
    }
    fetchCommercialOptions(
      project.mainContact?.organization?.id,
      controller.signal,
    )
      .then(setCommercialOptions)
      .catch((error) => {
        if (error.name === "CanceledError" || error.code === "ERR_CANCELED")
          return;
        console.log(error);
      });
    (async () => {
      try {
        const { data } = await API.get(`${API_END_POINT}api/spa-marques`, {
          signal: controller.signal,
        });
        setSpaMarques(
          (data?.data ?? []).map((row) => ({
            name: row.attributes?.name,
            code: row.id,
          })),
        );
      } catch (error) {
        if (error.name === "CanceledError" || error.code === "ERR_CANCELED")
          return;
        console.log(error);
      }
    })();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const spaOptions = (models ?? [])
    .filter(isSpaModel)
    .map(({ name, code }) => ({ name, code }));
  // Focus model: one of the interested spas when some are selected,
  // otherwise any spa model.
  const focusOptions = (models ?? [])
    .filter((model) =>
      formData.interestedModels.length
        ? formData.interestedModels.includes(model.code)
        : isSpaModel(model),
    )
    .map(({ name, code }) => ({ name, code }));

  const handleSave = () => {
    dispatch(
      updateProject(
        project.id,
        {
          number: formData.number,
          name: formData.name,
          comment: formData.comment,
          budget: formData.budget,
          interestedModels: formData.interestedModels,
          competitorBrands: formData.competitorBrands,
          focusModel: formData.focusModel?.code ?? null,
          salesRep: formData.commercial?.code ?? null,
          mainContactInfo: {
            lastName: formData.lastName,
            firstName: formData.firstName,
            email: formData.email,
            phoneNumber: formData.phoneNumber,
            contact: formData.contact,
          },
        },
        t,
      ),
    );
  };

  const labeled = (label, node) => (
    <div className="col-6" key={label}>
      <label className="f16 text-softBlack sBold mb-1">{t(label)}</label>
      {node}
    </div>
  );
  const textField = (label, name) =>
    labeled(
      label,
      <TextInput
        placeholder={t(label)}
        value={formData[name]}
        callback={(e) =>
          setFormData((prev) => ({ ...prev, [name]: e.target.value }))
        }
      />,
    );
  const multiSelectField = (label, name, options) =>
    labeled(
      label,
      <CustomMultiSelect
        name={name}
        className="w100"
        filter
        optionLabel="name"
        optionValue="code"
        placeholder={t(label)}
        options={options}
        value={formData[name]}
        callback={(e) =>
          setFormData((prev) => ({
            ...prev,
            [name]: e.value,
            ...(name === "interestedModels" &&
            prev.focusModel &&
            !e.value.includes(prev.focusModel.code)
              ? { focusModel: null }
              : {}),
          }))
        }
      />,
    );

  return (
    <div className="flex-1 d-flex flex-column">
      <div className="modalContent px-5 pt-4 pb-5">
        <div className="row g-3">
          {textField("Project number", "number")}
          {textField("Project name", "name")}
          {textField("Main contact last name", "lastName")}
          {textField("Main contact first name", "firstName")}
          {textField("Main contact email", "email")}
          {textField("Main contact phone", "phoneNumber")}
          {labeled(
            "Focus model",
            <Select
              placeholder={t("Focus model")}
              options={focusOptions}
              value={formData.focusModel}
              showClear
              resetable
              callback={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  focusModel: e.value ?? null,
                }))
              }
            />,
          )}
          {multiSelectField("Interested model", "interestedModels", spaOptions)}
          {labeled(
            "Contact",
            (() => {
              // Web-origin sources are locked; legacy free-text values stay
              // selectable so they still display.
              const options = prospectSourceOptions(t);
              if (
                formData.contact &&
                !options.some((option) => option.code === formData.contact)
              ) {
                options.unshift({
                  name: formData.contact,
                  code: formData.contact,
                });
              }
              return (
                <Select
                  placeholder={t("Contact")}
                  options={options}
                  value={
                    options.find(
                      (option) => option.code === formData.contact,
                    ) ?? null
                  }
                  disabled={AUTO_SOURCES.includes(mainContact.contact)}
                  showClear
                  resetable
                  callback={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      contact: e.value?.code ?? "",
                    }))
                  }
                />
              );
            })(),
          )}
          {multiSelectField("Competitor brand", "competitorBrands", spaMarques)}
          {textField("Budget", "budget")}
          {labeled(
            "Commercial",
            <Select
              placeholder={t("Commercial")}
              options={commercialOptions}
              value={formData.commercial}
              disabled={isCommercial}
              showClear
              resetable
              callback={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  commercial: e.value ?? null,
                }))
              }
            />,
          )}
          <div className="col-12">
            <label className="f16 text-softBlack sBold mb-1">
              {t("Comment")}
            </label>
            <TextArea
              placeholder={t("Comment")}
              rows={4}
              value={formData.comment}
              callback={(e) =>
                setFormData((prev) => ({ ...prev, comment: e.target.value }))
              }
            />
          </div>
        </div>
        {sale ? <ClientOrderBlock sale={sale} t={t} /> : null}
        <div className="mt-3 d-flex flex-wrap gap-3">
          {/* <Button variant="outline" onClick={() => setShowMagicLogin(true)}>
            {t("Invitation QR Code")}
          </Button> */}
          <Button
            variant="outline"
            loading={sendingEmail}
            Icon={() => <i className="bi bi-envelope" />}
            onClick={async () => {
              if (sendingEmail) return;
              setSendingEmail(true);
              await dispatch(sendProjectEmail(project.id, t));
              setSendingEmail(false);
            }}
          >
            {t("Send the mobile app invitation")}
          </Button>
        </div>
      </div>
      <div className="d-flex justify-content-end gap-3 px-5 py-3 border-top">
        <Button variant="outline" onClick={() => dispatch(closeRightModal())}>
          {t("Cancel")}
        </Button>
        <Button
          loading={updateLoading}
          onClick={updateLoading ? undefined : handleSave}
        >
          {t("Save")}
        </Button>
      </div>
      {showMagicLogin ? (
        <MagicLoginModal
          customer={mainContact}
          onClose={() => setShowMagicLogin(false)}
        />
      ) : null}
    </div>
  );
};

export default ProjectGeneral;
